import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdAdd, MdEdit, MdDelete, MdSearch } from 'react-icons/md';
import { Modal } from '../../../components/shared/Modal';
import { useToast } from '../../../components/shared/Toast';
import { authenticatedFetch } from '../../../lib/api';
import { WarehouseForm } from './WarehouseForm';
import { ClearFiltersButton, TableBodySkeleton, erpPageBtnPrimaryClass } from '@euroasiann/shared/ui';
import {
    pmTableWrapClass,
    pmThClass,
    pmTdClass,
    pmTrClass,
    pmIconBtnClass,
    pmDangerIconBtnClass,
    pmStatusClass,
} from '../port-management.ui';

interface Warehouse {
    _id: string;
    name: string;
    address: string;
    portId: {
        _id: string;
        name: string;
        code: string;
    };
    distanceFromPort: number;
    status: string;
}

export function WarehouseList() {
    const [search, setSearch] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
    const { success, error } = useToast();
    const queryClient = useQueryClient();

    const { data: warehouses = [], isLoading } = useQuery({
        queryKey: ['warehouses'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/warehouses');
            const data = await res.json();
            return data.data || [];
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await authenticatedFetch(`/api/v1/warehouses/${id}`, {
                method: 'DELETE',
            });
            if (!res.ok) throw new Error('Failed to delete warehouse');
        },
        onSuccess: () => {
            success('Warehouse deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['warehouses'] });
        },
        onError: (err: any) => {
            error(err.message);
        },
    });

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this warehouse?')) {
            deleteMutation.mutate(id);
        }
    };

    const filteredWarehouses = warehouses.filter((w: Warehouse) =>
        w.name.toLowerCase().includes(search.toLowerCase()) ||
        w.address.toLowerCase().includes(search.toLowerCase()) ||
        w.portId?.name?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="relative max-w-xl flex-1">
                        <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
                        <input
                            type="search"
                            placeholder="Search warehouses"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="h-10 w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-0 pl-9 pr-3 text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:border-[#0088cc] focus:outline-none focus:ring-1 focus:ring-[#0088cc]"
                        />
                    </div>
                    <ClearFiltersButton visible={!!search.trim()} onClick={() => setSearch('')} variant="toolbar" />
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setSelectedWarehouse(null);
                        setIsFormOpen(true);
                    }}
                    className={erpPageBtnPrimaryClass}
                >
                    <MdAdd className="h-5 w-5" />
                    Add warehouse
                </button>
            </div>

            <div className={pmTableWrapClass}>
                <table className="w-full min-w-[720px] border-collapse">
                    <thead>
                        <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
                            {['Warehouse', 'Port', 'Distance', 'Status', 'Actions'].map((h) => (
                                <th key={h} className={pmThClass}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <TableBodySkeleton rows={6} cols={5} />
                        ) : filteredWarehouses.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">
                                    No warehouses found
                                </td>
                            </tr>
                        ) : filteredWarehouses.map((w: Warehouse) => (
                            <tr key={w._id} className={pmTrClass}>
                                <td className={pmTdClass}>
                                    <div className="font-medium">{w.name}</div>
                                    <div className="max-w-[240px] truncate text-xs text-[hsl(var(--muted-foreground))]">{w.address}</div>
                                </td>
                                <td className={pmTdClass}>
                                    <div>{w.portId?.name}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{w.portId?.code}</div>
                                </td>
                                <td className={pmTdClass}>
                                    {w.distanceFromPort != null ? `${w.distanceFromPort} km` : '—'}
                                </td>
                                <td className={pmTdClass}>
                                    <span className={pmStatusClass(w.status === 'Active')}>{w.status}</span>
                                </td>
                                <td className={pmTdClass}>
                                    <div className="flex items-center gap-0.5">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedWarehouse(w);
                                                setIsFormOpen(true);
                                            }}
                                            className={pmIconBtnClass}
                                        >
                                            <MdEdit className="h-4 w-4" />
                                        </button>
                                        <button type="button" onClick={() => handleDelete(w._id)} className={pmDangerIconBtnClass}>
                                            <MdDelete className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                title={selectedWarehouse ? 'Edit warehouse' : 'Add warehouse'}
            >
                <WarehouseForm
                    initialData={selectedWarehouse}
                    onSuccess={() => {
                        setIsFormOpen(false);
                        queryClient.invalidateQueries({ queryKey: ['warehouses'] });
                    }}
                    onCancel={() => setIsFormOpen(false)}
                />
            </Modal>
        </div>
    );
}
